import { StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { RouteMapPin } from '@/types/route-plan';

type RoutePinCalloutProps = {
  /** All pins of one stack, sorted by stepNumber ascending. */
  pins: RouteMapPin[];
};

/**
 * Callout body for a pin stack: one row per action at the location with
 * step number, title, arrive time, and state.
 */
export function RoutePinCallout({ pins }: RoutePinCalloutProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const heading =
    pins.length > 1 ? `${pins[0].title} · ${pins.length} steps here` : pins[0].title;

  return (
    <View style={[styles.container, { backgroundColor: palette.cardBackground }]}>
      <Text
        style={[styles.heading, { color: palette.text, fontFamily: getBodyFont() }]}
        numberOfLines={1}
      >
        {heading}
      </Text>
      {pins.map((pin) => (
        <View key={pin.id} style={styles.row}>
          <StateIcon state={pin.state} color={palette.primary} muted={palette.textMuted} />
          <Text
            style={[
              styles.rowText,
              { color: pin.state === 'done' ? palette.textMuted : palette.text },
              pin.state === 'current' && styles.currentText,
              { fontFamily: getBodyFont() },
            ]}
            numberOfLines={1}
          >
            {pin.stepNumber} · {pin.title}
          </Text>
          <Text style={[styles.time, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
            {pin.timeLabel}
          </Text>
        </View>
      ))}
    </View>
  );
}

function StateIcon({ state, color, muted }: { state: string; color: string; muted: string }) {
  if (state === 'done') return <Ionicons name="checkmark-circle" size={14} color={color} />;
  if (state === 'current') return <Ionicons name="play-circle" size={14} color={color} />;
  return <Ionicons name="ellipse-outline" size={14} color={muted} />;
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BorderRadius.md,
    padding: Spacing.sm,
    minWidth: 180,
    maxWidth: 240,
    gap: Spacing.xxs,
  },
  heading: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xxs,
  },
  rowText: {
    fontSize: 11,
    flexShrink: 1,
  },
  currentText: {
    fontWeight: '700',
  },
  time: {
    fontSize: 10,
    marginLeft: 'auto',
  },
});
